import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../utils/config";
import {
  codeToDigits,
  normalizeVerificationCode,
} from "../../utils/loginVerifyUtils";
import { clearDeepLinkVerificationCode } from "../../utils/deepLinkVerificationCode";
import { subscribeConsent } from "../../utils/consent";
import { trackRegistration } from "../../utils/klaviyo";
import "./Register.css";

const baseUrl = API_URL;

const VerifyCode = ({ email, initialCode = "" }) => {
  const { t } = useTranslation("register");
  const navigate = useNavigate();
  const CODE_LENGTH = 6;

  const [codeDigits, setCodeDigits] = useState(() => codeToDigits(initialCode));
  const [verifyError, setVerifyError] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [resendError, setResendError] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [timerRunning, setTimerRunning] = useState(true);
  const [timeLeft, setTimeLeft] = useState(120000);
  const [isVerified, setIsVerified] = useState(false);

  const inputRefs = useRef([]);
  const hasAutoSubmittedRef = useRef(false);

  useEffect(() => {
    if (!isVerified) return;
    const send = () => trackRegistration("completed", "Vendor", email);
    send();
    return subscribeConsent(send);
  }, [isVerified, email]);

  const getLangFromLocalStorage = () =>
    localStorage.getItem("language") || "en";

  useEffect(() => {
    let timer;
    if (timerRunning && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1000) {
            clearInterval(timer);
            setTimerRunning(false);
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timerRunning, timeLeft]);

  const handleDigitChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);
    setVerifyError("");
    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const verifyCodeAPI = async (code) => {
    const res = await fetch(`${baseUrl}/api/v1/attnd-supplier/auth/verify-code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        lang: getLangFromLocalStorage(),
      },
      body: JSON.stringify({ email, code, type: "register" }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(
        err.message || err.error || t("verifyCode.messages.verifyError"),
      );
    }
    return res.json();
  };

  const submitVerification = useCallback(
    async (rawCode) => {
      const code = normalizeVerificationCode(rawCode);
      if (code.length !== CODE_LENGTH) {
        setVerifyError(t("verifyCode.messages.verifyIncomplete"));
        return;
      }
      if (isVerifying) {
        return;
      }

      setIsVerifying(true);
      setVerifyError("");

      try {
        const data = await verifyCodeAPI(code);
        if (data.message) {
          clearDeepLinkVerificationCode("register", email);
          trackRegistration("completed", "Vendor", email);
          setIsVerified(true);
          setTimeout(() => navigate("/login"), 2000);
        } else {
          setVerifyError(t("verifyCode.messages.verifyInvalid"));
        }
      } catch (err) {
        setVerifyError(err.message || t("verifyCode.messages.verifyError"));
      } finally {
        setIsVerifying(false);
      }
    },
    [email, isVerifying, navigate, t],
  );

  const handleVerifyCodeSubmit = async (e) => {
    e.preventDefault();
    await submitVerification(codeDigits.join(""));
  };

  useEffect(() => {
    const normalized = normalizeVerificationCode(initialCode);
    if (normalized.length !== CODE_LENGTH) {
      return;
    }
    setCodeDigits(normalized.split(""));
  }, [initialCode]);

  useEffect(() => {
    const normalized = normalizeVerificationCode(initialCode);
    if (
      hasAutoSubmittedRef.current ||
      normalized.length !== CODE_LENGTH ||
      !email
    ) {
      return;
    }

    hasAutoSubmittedRef.current = true;
    submitVerification(normalized);
  }, [initialCode, email, submitVerification]);

  const resendCodeAPI = async () => {
    const res = await fetch(`${baseUrl}/api/v1/attnd-supplier/auth/resend-code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        lang: getLangFromLocalStorage(),
      },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(
        err.message || err.error || t("verifyCode.messages.resendError"),
      );
    }
    return res.json();
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setResendError("");
    setResendMessage("");
    try {
      const data = await resendCodeAPI();
      if (data.message) {
        setResendMessage(data.message || t("verifyCode.messages.resendSuccess"));
        setTimeLeft(120000);
        setTimerRunning(true);
      }
    } catch (err) {
      setResendError(err.message || t("verifyCode.messages.resendError"));
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  if (isVerified) {
    return (
      <div className="success-message">
        <img src="/tick-circle.svg" alt="Success" className="success-icon" />
        <h1>{t("verifyCode.titles.successLine1")}</h1>
        <p style={{ marginBottom: "20px", textAlign: "center" }}>
          {t("verifyCode.subtitles.success")}
        </p>
        <button
          className="login-btn"
          type="button"
          onClick={() => navigate("/login")}
        >
          {t("verifyCode.buttons.login")}
        </button>
      </div>
    );
  }

  return (
    <div className="verify-code-container">
      <h2 className="title-login">{t("verifyCode.titles.verify")}</h2>
      <p className="subtitle-login">{t("verifyCode.subtitles.verify")}</p>

      <form onSubmit={handleVerifyCodeSubmit}>
        <div className="form-group-login">
          <div className="digits-container">
            {codeDigits.map((digit, idx) => (
              <input
                key={idx}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleDigitChange(e.target.value, idx)}
                onFocus={(e) => e.target.select()}
                ref={(el) => (inputRefs.current[idx] = el)}
                className="digit-input"
                inputMode="numeric"
                disabled={isVerifying}
              />
            ))}
          </div>
          {verifyError && <p className="error">{verifyError}</p>}
        </div>

        <div className="form-group-login">
          <button
            className="login-btn"
            type="submit"
            disabled={
              codeDigits.some((d) => d === "") || isVerifying
            }
          >
            {isVerifying
              ? t("verifyCode.buttons.submitting", {
                  defaultValue: t("verifyCode.buttons.submit"),
                })
              : t("verifyCode.buttons.submit")}
          </button>
        </div>

        {resendMessage && <p className="resend-btn">{resendMessage}</p>}
        {resendError && <p className="error">{resendError}</p>}
      </form>

      <div className="form-group-login">
        {timerRunning ? (
          <p className="resend-btn">
            {t("verifyCode.messages.resendIn", {
              time: formatTime(timeLeft / 1000),
            })}
          </p>
        ) : (
          <button
            className="resend-btn"
            type="button"
            onClick={handleResendCode}
            disabled={isResending}
          >
            {isResending
              ? t("verifyCode.buttons.resending")
              : t("verifyCode.buttons.resend")}
          </button>
        )}
      </div>
    </div>
  );
};

export default VerifyCode;
